import React, { useState, useEffect, useRef } from 'react';
import { Modal, Table, Button, Tag, Space, Progress, message, Tooltip, Popconfirm } from 'antd';
import { ReloadOutlined, CheckCircleOutlined, CloseCircleOutlined, LoadingOutlined, PlayCircleOutlined, DeleteOutlined, CodeOutlined } from '@ant-design/icons';
import { authAxios } from '../utils/session_utils';
import { YouTubeLiveStream, TranscodeJob, TranscodeJobStatus } from './types';

interface TranscodeJobsModalProps {
  stream: YouTubeLiveStream | null;
  open: boolean;
  onClose: () => void;
}

const TranscodeJobsModal: React.FC<TranscodeJobsModalProps> = ({ 
  stream, 
  open, 
  onClose 
}) => {
  const [jobs, setJobs] = useState<TranscodeJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState<{ [key: string]: boolean }>({});
  const [deleting, setDeleting] = useState<{ [key: string]: boolean }>({});
  const [viewingJobConfig, setViewingJobConfig] = useState<any | null>(null);
  const autoRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (open && stream) {
      loadJobs();
    } else {
      stopAutoRefresh();
    }

    return () => {
      stopAutoRefresh();
    };
  }, [open, stream]);

  useEffect(() => {
    // Set up auto-refresh when jobs change
    if (open && jobs.length > 0) {
      const inProgressJobs = jobs.filter(job => 
        job.status === 'SUBMITTED' || job.status === 'PROGRESSING'
      );
      
      if (inProgressJobs.length > 0) {
        // Start auto-refresh if not already running
        if (!autoRefreshIntervalRef.current) {
          startAutoRefresh();
        }
      } else {
        stopAutoRefresh();
      }
    } else {
      stopAutoRefresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, open]);

  const loadJobs = async () => {
    if (!stream) return;
    
    setLoading(true);
    try {
      const response = await authAxios({
        method: 'GET',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/streams/${stream.id}/transcode-jobs`
      });
      const jobsData = response.data.jobs || [];
      
      // Enrich jobs with video titles from stream if available
      const enrichedJobs = jobsData.map((job: any) => {
        const enrichedJob: TranscodeJob = {
          ...job,
          status: job.status as TranscodeJobStatus,
        };
        if (stream.videos && enrichedJob.video_id) {
          const video = stream.videos.find((v: any) => v.video_id === enrichedJob.video_id);
          if (video) {
            enrichedJob.video_title = video.video_title || video.video_file_name;
          }
        }
        return enrichedJob;
      });
      
      setJobs(enrichedJobs);
    } catch (error: any) {
      console.error('Error loading transcoding jobs:', error);
      message.error('Failed to load transcoding jobs');
    } finally {
      setLoading(false);
    }
  };

  const refreshJob = async (jobId: string) => {
    setRefreshing(prev => ({ ...prev, [jobId]: true }));
    try {
      const response = await authAxios({
        method: 'POST',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/transcode-jobs/${jobId}/refresh`
      });
      
      // Update the job in the list
      setJobs(prev => prev.map(job => 
        job.id === jobId ? { ...job, ...response.data, status: response.data.status as TranscodeJobStatus } : job
      ));
      
      message.success('Job status refreshed');
    } catch (error: any) {
      console.error('Error refreshing job:', error);
      message.error('Failed to refresh job status');
    } finally {
      setRefreshing(prev => ({ ...prev, [jobId]: false }));
    }
  };

  const refreshAllJobs = async () => {
    const inProgressJobs = jobs.filter(job => 
      job.status === 'SUBMITTED' || job.status === 'PROGRESSING'
    );
    
    if (inProgressJobs.length === 0) {
      message.info('No jobs in progress to refresh');
      return;
    }

    setLoading(true);
    try {
      await Promise.all(
        inProgressJobs.map(job => refreshJob(job.id))
      );
      message.success('All jobs refreshed');
    } catch (error) {
      console.error('Error refreshing all jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteJob = async (jobId: string) => {
    setDeleting(prev => ({ ...prev, [jobId]: true }));
    try {
      await authAxios({
        method: 'DELETE',
        url: `${process.env.REACT_APP_BACK_END_API}/youtube-live/transcode-jobs/${jobId}`
      });
      
      // Remove the job from the list
      setJobs(prev => prev.filter(job => job.id !== jobId));
      
      message.success('Job deleted successfully');
    } catch (error: any) {
      console.error('Error deleting job:', error);
      if (error.response?.data?.error) {
        message.error(error.response.data.error);
      } else {
        message.error('Failed to delete job');
      }
    } finally {
      setDeleting(prev => ({ ...prev, [jobId]: false }));
    }
  };

  const startAutoRefresh = () => {
    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
    }
    
    autoRefreshIntervalRef.current = setInterval(() => {
      setJobs(currentJobs => {
        const inProgressJobs = currentJobs.filter(job => 
          job.status === 'SUBMITTED' || job.status === 'PROGRESSING'
        );
        
        if (inProgressJobs.length > 0) {
          inProgressJobs.forEach(job => {
            refreshJob(job.id).catch(console.error);
          });
        } else {
          stopAutoRefresh();
        }
        
        return currentJobs;
      });
    }, 10000); // Refresh every 10 seconds
  };

  const stopAutoRefresh = () => {
    if (autoRefreshIntervalRef.current) {
      clearInterval(autoRefreshIntervalRef.current);
      autoRefreshIntervalRef.current = null;
    }
  };

  const getStatusColor = (status: TranscodeJobStatus) => {
    switch (status) {
      case 'COMPLETE': return 'success';
      case 'PROGRESSING': return 'processing';
      case 'SUBMITTED': return 'default';
      case 'ERROR': return 'error';
      case 'CANCELED': return 'default';
      default: return 'default';
    }
  };

  const getStatusIcon = (status: TranscodeJobStatus) => {
    switch (status) {
      case 'COMPLETE':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
      case 'ERROR':
        return <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
      case 'PROGRESSING':
      case 'SUBMITTED':
        return <LoadingOutlined style={{ color: '#1890ff' }} />;
      default:
        return null;
    }
  };

  const columns = [
    {
      title: 'Video',
      dataIndex: 'video_title',
      key: 'video_title',
      render: (text: string, record: TranscodeJob) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text || record.video_id || 'Unknown'}</div>
          {record.video_id && (
            <div style={{ fontSize: '11px', color: '#999' }}>ID: {record.video_id}</div>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: TranscodeJobStatus) => (
        <Space>
          {getStatusIcon(status)}
          <Tag color={getStatusColor(status)}>{status}</Tag>
        </Space>
      ),
    },
    {
      title: 'Progress',
      key: 'progress',
      render: (record: TranscodeJob) => {
        if (record.status === 'PROGRESSING' || record.status === 'SUBMITTED') {
          return (
            <Progress 
              percent={record.progress_percent || 0} 
              status="active"
              size="small"
            />
          );
        }
        if (record.status === 'COMPLETE') {
          return <Tag color="success">100%</Tag>;
        }
        return '-';
      },
    },
    {
      title: 'Output URL',
      dataIndex: 'output_url',
      key: 'output_url',
      render: (url: string) => {
        if (!url) return <span style={{ color: '#999' }}>Not available</span>;
        return (
          <Tooltip title={url}>
            <a href={url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '11px' }}>
              {url.length > 50 ? `${url.substring(0, 50)}...` : url}
            </a>
          </Tooltip>
        );
      },
    },
    {
      title: 'Error',
      dataIndex: 'error_message',
      key: 'error_message',
      render: (error: string) => {
        if (!error) return '-';
        return (
          <Tooltip title={error}>
            <Tag color="error" style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {error.length > 30 ? `${error.substring(0, 30)}...` : error}
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: TranscodeJob) => (
        <Space>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => refreshJob(record.id)}
            loading={refreshing[record.id]}
            disabled={record.status === 'COMPLETE' || record.status === 'CANCELED'}
          >
            Refresh
          </Button>
          {record.input_job_config && (
            <Tooltip title="View MediaConvert job configuration">
              <Button
                size="small"
                icon={<CodeOutlined />}
                onClick={() => setViewingJobConfig(record.input_job_config)}
              >
                View JSON
              </Button>
            </Tooltip>
          )}
          {(record.status === 'ERROR' || record.status === 'COMPLETE') && (
            <Popconfirm
              title="Delete this job?"
              description="Are you sure you want to delete this job? This action cannot be undone."
              onConfirm={() => deleteJob(record.id)}
              okText="Yes, Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            >
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                loading={deleting[record.id]}
              >
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={
          <Space>
            <PlayCircleOutlined />
            <span>Transcoding Jobs - {stream?.name}</span>
          </Space>
        }
        open={open}
        onCancel={onClose}
        footer={[
          <Button key="refresh-all" icon={<ReloadOutlined />} onClick={refreshAllJobs} loading={loading}>
            Refresh All
          </Button>,
          <Button key="close" type="primary" onClick={onClose}>
            Close
          </Button>
        ]}
        width={1200}
      >
        <div style={{ marginTop: '16px' }}>
          {jobs.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              No transcoding jobs found for this stream.
            </div>
          ) : (
            <Table
              columns={columns}
              dataSource={jobs}
              rowKey="id"
              loading={loading}
              pagination={false}
              size="small"
            />
          )}
        </div>
      </Modal>

      {/* JSON Viewer Modal */}
      <Modal
        title={
          <Space>
            <CodeOutlined />
            <span>MediaConvert Job Configuration</span>
          </Space>
        }
        open={viewingJobConfig !== null}
        onCancel={() => setViewingJobConfig(null)}
        footer={[
          <Button 
            key="copy" 
            onClick={() => {
              navigator.clipboard.writeText(JSON.stringify(viewingJobConfig, null, 2));
              message.success('JSON copied to clipboard');
            }}
          >
            Copy to Clipboard
          </Button>,
          <Button key="close" type="primary" onClick={() => setViewingJobConfig(null)}>
            Close
          </Button>
        ]}
        width={900}
      >
        <div style={{ 
          maxHeight: '600px', 
          overflow: 'auto', 
          backgroundColor: '#f5f5f5', 
          padding: '16px', 
          borderRadius: '4px',
          fontFamily: 'monospace',
          fontSize: '12px'
        }}>
          <pre style={{ margin: 0 }}>
            {JSON.stringify(viewingJobConfig, null, 2)}
          </pre>
        </div>
      </Modal>
    </>
  );
};

export default TranscodeJobsModal;
